import React, { useState, useEffect, useRef } from "react";
import { Stack, FormGroup, Box, Modal } from "@mui/material";
import checkImg from "../../../assets/images/check_circle.svg";
import closeIcon from "../../../assets/images/closeicon.svg";
import "../../../assets/styles/checkboxRadio.scss";
import AccessibleTooltip from "../../Tooltip/Tooltip.js";
import {
  style,
  AntSwitch,
  fetchSettings,
  updateSettings,
  handleEscapeKey,
  decodeUserRole,
} from "../../../utils/securitySettingsUtils.js";

const SecuritySettings = () => {
  const [settings, setSettings] = useState({});
  const [initialSettings, setInitialSettings] = useState({});
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [userRole, setUserRole] = useState("");
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const firstInputRef = useRef(null);

  useEffect(() => {
    setUserRole(decodeUserRole());
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (showSuccessDialog && firstInputRef.current) {
        firstInputRef.current.focus();
      }
    }, 200);
    return () => clearTimeout(timeoutId);
  }, [showSuccessDialog]);

  useEffect(() => {
    const onKeyDown = (e) => handleEscapeKey(e, tooltipOpen, setTooltipOpen);
    if (showSuccessDialog) window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showSuccessDialog, tooltipOpen]);

  const canEditSettings = userRole === "SUPER_ADMIN";

  useEffect(() => {
    (async () => {
      const fetched = await fetchSettings();
      setSettings(fetched);
      setInitialSettings(fetched);
    })();
  }, []);

  const hasChanges = Object.entries(settings)
    .filter(([key]) => key)
    .some(([key, value]) => value !== initialSettings[key]);

  const handleSubmit = async () => {
    const result = await updateSettings(settings, initialSettings);
    if (result.success) {
      setShowSuccessDialog(true);
      setInitialSettings({ ...settings });
    } else {
      setShowSuccessDialog(false);
    }
  };

  const handleCloseSuccessDialog = () => {
    setShowSuccessDialog(false);
    setTimeout(() => {
      document.getElementById("tab-securitySetting")?.focus();
    }, 200);
  };

  const handleToggle = (key) => {
    if (!canEditSettings) return;
    setSettings((prev) => ({
      ...prev,
      [key]: prev[key] === "1" ? "0" : "1",
    }));
  };

  return (
    <div className="notifications_wrapper" role="region">
      <div className="gl_toggle">
        <div className="sc_heading">
          <p id="label-Multi_Factor_Authentication">
            Multi Factor Authentication
          </p>
          <span>
            If enabled, the user will have to go through multi factor
            authentication when logging in.
          </span>
        </div>

        <div
          className="sc_toggle"
          tabIndex={!canEditSettings ? -1 : 0}
          role="switch"
          aria-disabled={!canEditSettings}
          aria-checked={settings.mfa_enabled === "1"}
          aria-labelledby="label-Multi_Factor_Authentication"
          onClick={() => handleToggle("mfa_enabled")}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleToggle("mfa_enabled");
            }
          }}
        >
          <FormGroup>
            <Stack direction="row" spacing={1} alignItems="center">
              <AntSwitch
                checked={settings.mfa_enabled === "1"}
                inputProps={{
                  "aria-hidden": true,
                  tabIndex: -1,
                }}
                disabled={!canEditSettings}
              />
            </Stack>
          </FormGroup>
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
        <button
          type="submit"
          className={`containedButton ${!hasChanges ? "containdBtnDisabled" : ""}`}
          aria-label="Save"
          onClick={handleSubmit}
          disabled={!hasChanges}
        >
          Save
        </button>
      </div>

      <Modal
        open={showSuccessDialog}
        onClose={(event, reason) => {
          if (reason === "backdropClick") return;
          handleCloseSuccessDialog();
        }}
        disableEscapeKeyDown={tooltipOpen}
        className="globalForm"
      >
        <div role="none">
          <Box
            role="dialog"
            aria-modal="true"
            sx={style}
            aria-labelledby="add-gl-mfa-title"
            className="mainModalBox"
          >
            <div className="Modal_header" style={{ width: "100%" }}>
              <h1 id="add-gl-mfa-title" data-testId="dd-gl-mfa-title">
                Security Setting
              </h1>
              <AccessibleTooltip
                title="Close"
                open={tooltipOpen}
                customclass="modalcloseTooltip sucessModaltooltip"
              >
                <button
                  ref={firstInputRef}
                  onClick={handleCloseSuccessDialog}
                  className="close_button_modal"
                  aria-label="Close"
                  onFocus={() => setTooltipOpen(true)}
                  onBlur={() => setTooltipOpen(false)}
                  onMouseEnter={() => setTooltipOpen(true)}
                  onMouseLeave={() => setTooltipOpen(false)}
                >
                  <img src={closeIcon} alt="" aria-hidden="true" />
                </button>
              </AccessibleTooltip>
            </div>
            <div className="Modal_middle">
              <div
                className="emailsent-wrapper manageUser_details"
                role="alert"
              >
                <img src={checkImg} alt="" aria-hidden="true" />
                <h2>Success</h2>
                <p>All users will be required to use MFA on their next login</p>
              </div>
            </div>
          </Box>
        </div>
      </Modal>
    </div>
  );
};

export default SecuritySettings;
